import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Loader2Icon,
  UserPlusIcon,
  ArrowLeftIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  fetchChatChannels,
  fetchChatMessages,
  sendChatMessage,
  uploadChatAttachment,
  markChannelRead,
  ensureDirectChatChannel,
  fetchChatDirectoryUsers,
  chatSenderLabel,
  type ChatChannelListItem,
  type ChatMessage,
  type ChatDirectoryUser,
} from '../../api/teamChat'
import { usePermissions } from '../../hooks/usePermissions'
import { canCompleteJobWithException } from '../../lib/jobCompletionException'
import { MobilePage, MobilePageHeader, MobileSheet, MobileSearchField, mobile } from '../mobile'
import { ChatComposeBar } from './ChatComposeBar'
import { ChatMessageBody } from './ChatMessageBody'
import { supabase } from '../../lib/supabase'
import { getErrorMessage } from '../../lib/errors'

function channelSection(
  channels: ChatChannelListItem[],
  type: ChatChannelListItem['channel_type']
) {
  return channels.filter((c) => c.channel_type === type)
}

export interface TeamChatPanelProps {
  embedded?: boolean
  /** Prefill / deep-link channel from Messages hub. */
  initialChannelId?: string | null
  onActiveChannelChange?: (channelId: string | null) => void
}

export function TeamChatPanel({
  embedded = false,
  initialChannelId = null,
  onActiveChannelChange,
}: TeamChatPanelProps) {
  const { t } = useTranslation('field')
  const { can, loading: permLoading, isAdmin, profile } = usePermissions()
  const canChat = can('communication.chat')
  const canInbox = can('communication.inbox')
  const canOversee =
    isAdmin ||
    canCompleteJobWithException(profile?.role) ||
    can('jobs.dispatch') ||
    can('review.view')

  const [channels, setChannels] = useState<ChatChannelListItem[]>([])
  const [activeId, setActiveId] = useState<string | null>(initialChannelId)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [body, setBody] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [showDmPicker, setShowDmPicker] = useState(false)
  const [directory, setDirectory] = useState<ChatDirectoryUser[]>([])
  const [mobileShowThread, setMobileShowThread] = useState(Boolean(initialChannelId))
  const listRef = useRef<HTMLDivElement>(null)
  const activeIdRef = useRef<string | null>(activeId)

  useEffect(() => {
    activeIdRef.current = activeId
    onActiveChannelChange?.(activeId)
  }, [activeId, onActiveChannelChange])

  useEffect(() => {
    if (initialChannelId && initialChannelId !== activeId) {
      setActiveId(initialChannelId)
      setMobileShowThread(true)
      loadMessages(initialChannelId).catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialChannelId])

  const active = useMemo(
    () => channels.find((c) => c.id === activeId) || null,
    [channels, activeId]
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return channels
    return channels.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        (c.subtitle || '').toLowerCase().includes(q) ||
        (c.job_number || '').toLowerCase().includes(q)
    )
  }, [channels, query])

  const loadChannels = useCallback(async () => {
    const rows = await fetchChatChannels()
    setChannels(rows)
    return rows
  }, [])

  const loadMessages = useCallback(async (cid: string) => {
    const rows = await fetchChatMessages(cid)
    setMessages(rows)
    await markChannelRead(cid)
  }, [])

  const init = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await loadChannels()
      const prefer =
        (initialChannelId && rows.some((c) => c.id === initialChannelId) && initialChannelId) ||
        (activeId && rows.some((c) => c.id === activeId) && activeId) ||
        rows.find((c) => c.channel_type === 'general')?.id ||
        rows[0]?.id ||
        null
      setActiveId(prefer)
      if (prefer) await loadMessages(prefer)
      else setMessages([])
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load chat'))
    } finally {
      setLoading(false)
    }
  }, [activeId, initialChannelId, loadChannels, loadMessages])

  useEffect(() => {
    if (permLoading || (!canChat && !canInbox)) return
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only when chat becomes available
  }, [permLoading, canChat, canInbox])

  // Poll backup + prefer realtime for active channel
  useEffect(() => {
    if (!activeId) return
    const id = window.setInterval(() => {
      loadMessages(activeId).catch(() => {})
      loadChannels().catch(() => {})
    }, 30000)
    return () => window.clearInterval(id)
  }, [activeId, loadMessages, loadChannels])

  useEffect(() => {
    if (!activeId) return
    const channel = supabase
      .channel(`team-chat-live-${activeId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `channel_id=eq.${activeId}`,
        },
        () => {
          const cid = activeIdRef.current
          if (cid) loadMessages(cid).catch(() => {})
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }, [activeId, loadMessages])

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  const selectChannel = async (id: string) => {
    setActiveId(id)
    setMobileShowThread(true)
    setError(null)
    try {
      await loadMessages(id)
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load messages'))
    }
  }

  const handleSend = async (file?: File | null, mentionedUserIds?: string[]) => {
    if (!activeId || sending) return
    if (!body.trim() && !file) return
    setSending(true)
    setError(null)
    try {
      let attachmentUrl: string | null = null
      if (file) {
        attachmentUrl = await uploadChatAttachment(activeId, file)
      }
      await sendChatMessage(activeId, body, attachmentUrl, mentionedUserIds)
      setBody('')
      await loadMessages(activeId)
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to send message'))
    } finally {
      setSending(false)
    }
  }

  const openDmPicker = async () => {
    setShowDmPicker(true)
    try {
      setDirectory(await fetchChatDirectoryUsers())
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load directory'))
    }
  }

  const startDm = async (userId: string) => {
    try {
      const cid = await ensureDirectChatChannel(userId)
      setShowDmPicker(false)
      const rows = await loadChannels()
      setActiveId(cid)
      setMobileShowThread(true)
      if (!rows.some((c) => c.id === cid)) await loadChannels()
      await loadMessages(cid)
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to open DM'))
    }
  }

  if (permLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!canChat && !canInbox) {
    return <p className="text-gray-600 p-4">You do not have permission to use team chat.</p>
  }

  const renderChannelButton = (ch: ChatChannelListItem) => (
    <button
      key={ch.id}
      type="button"
      onClick={() => selectChannel(ch.id)}
      className={`w-full text-left px-3 py-3.5 min-h-[52px] rounded-xl text-sm transition-colors ${
        activeId === ch.id ? 'bg-blue-50 text-blue-900' : 'active:bg-gray-50 text-gray-800'
      }`}
    >
      <div className="font-semibold truncate">{ch.label}</div>
      {ch.subtitle && <div className="text-xs text-gray-500 truncate mt-0.5">{ch.subtitle}</div>}
    </button>
  )

  const shellClass = embedded
    ? 'flex flex-col md:flex-row h-full min-h-[360px] overflow-hidden bg-white'
    : `${mobile.surface} flex flex-col md:flex-row h-[calc(100dvh-10.5rem)] md:h-[calc(100vh-10rem)] min-h-[360px] overflow-hidden`

  const panel = (
    <>
      <div className={embedded ? 'h-full' : 'max-w-6xl mx-auto'}>
        {!embedded && (
          <div className={mobileShowThread ? 'hidden md:block mb-2' : 'mb-2'}>
            <MobilePageHeader
              title={t('chat.title')}
              subtitle={
                <span className="hidden sm:inline">
                  {canOversee ? t('chat.subtitleOversee') : t('chat.subtitleCrew')}
                </span>
              }
            />
          </div>
        )}

        <div className={shellClass}>
          <aside
            className={`md:w-72 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col min-h-0 ${
              mobileShowThread ? 'hidden md:flex' : 'flex'
            }`}
          >
            <div className="p-3 border-b border-gray-100 space-y-2">
              <MobileSearchField
                value={query}
                onChange={setQuery}
                placeholder={t('chat.searchPlaceholder')}
              />
              {canChat && (
                <button
                  type="button"
                  onClick={openDmPicker}
                  className={`${mobile.secondaryBtn} w-full text-sm`}
                >
                  <UserPlusIcon className="w-4 h-4" />
                  {t('chat.newDm')}
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-3">
              {loading && channels.length === 0 ? (
                <div className="flex justify-center py-8">
                  <Loader2Icon className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  {canChat && (
                    <>
                      <div>
                        <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                          General
                        </p>
                        {channelSection(filtered, 'general').map(renderChannelButton)}
                      </div>
                      <div>
                        <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                          Jobs
                        </p>
                        {channelSection(filtered, 'job').length === 0 ? (
                          <p className="px-2 text-xs text-gray-400">No job threads yet</p>
                        ) : (
                          channelSection(filtered, 'job').map(renderChannelButton)
                        )}
                      </div>
                      <div>
                        <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                          Direct
                        </p>
                        {channelSection(filtered, 'direct').length === 0 ? (
                          <p className="px-2 text-xs text-gray-400">No direct messages yet</p>
                        ) : (
                          channelSection(filtered, 'direct').map(renderChannelButton)
                        )}
                      </div>
                    </>
                  )}
                  {(canInbox || canChat) && (
                    <div>
                      <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                        Inbox discussions
                      </p>
                      {channelSection(filtered, 'inbox').length === 0 ? (
                        <p className="px-2 text-xs text-gray-400">No inbox discussions yet</p>
                      ) : (
                        channelSection(filtered, 'inbox').map(renderChannelButton)
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </aside>

          <section
            className={`flex-1 flex flex-col min-h-0 min-w-0 ${
              mobileShowThread ? 'flex' : 'hidden md:flex'
            }`}
          >
            <div className="px-3 sm:px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <button
                type="button"
                className="md:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600"
                onClick={() => setMobileShowThread(false)}
                aria-label="Back to channels"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h2 className="font-semibold text-gray-900 truncate">{active?.label || 'Select a channel'}</h2>
                {active?.subtitle && <p className="text-xs text-gray-500 truncate">{active.subtitle}</p>}
              </div>
            </div>

            {error && (
              <div className="px-4 py-2 text-sm text-red-700 bg-red-50 border-b border-red-100">{error}</div>
            )}

            <div ref={listRef} className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
              {loading && messages.length === 0 ? (
                <div className="flex justify-center py-12">
                  <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : !activeId ? (
                <p className="text-sm text-gray-500 text-center py-12">Select a channel to start.</p>
              ) : messages.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-12">No messages yet. Say hello.</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className="bg-gray-50 rounded-lg px-3 sm:px-4 py-3">
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="text-xs font-medium text-gray-700">{chatSenderLabel(m)}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {new Date(m.created_at).toLocaleString()}
                      </span>
                    </div>
                    <ChatMessageBody body={m.body} attachmentUrl={m.attachment_url} />
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-gray-200 p-3 sm:p-4">
              <ChatComposeBar
                value={body}
                onChange={setBody}
                onSend={handleSend}
                disabled={!activeId}
                sending={sending}
                placeholder={activeId ? t('chat.typeMessage') : t('chat.subtitle')}
              />
            </div>
          </section>
        </div>
      </div>

      <MobileSheet open={showDmPicker} onClose={() => setShowDmPicker(false)} title="Message someone">
        {directory.length === 0 ? (
          <p className="text-sm text-gray-500 p-3">No people found.</p>
        ) : (
          <div className="space-y-0.5 -mx-1">
            {directory.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => startDm(u.id)}
                className="w-full text-left px-3 py-3 min-h-[52px] rounded-xl active:bg-gray-50 text-sm"
              >
                <div className="font-semibold text-gray-900">
                  {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
                </div>
                <div className="text-xs text-gray-500">
                  {u.role}
                  {u.email ? ' · ' + u.email : ''}
                </div>
              </button>
            ))}
          </div>
        )}
      </MobileSheet>
    </>
  )

  if (embedded) {
    return <div className="h-full min-h-0 flex flex-col">{panel}</div>
  }

  return <MobilePage tight className="!pb-0">{panel}</MobilePage>
}
